import queryString from "query-string";
import Cookies from "js-cookie";
import Auth from "./AmplifyAuth";

export const VIEW_ONLY_TOKEN_KEY = "VIEW_ONLY_TOKEN_KEY";
const SIGNOUT_USER = "SIGNOUT_USER";
export const RECAP_DATA_CHUNK_KEY_PREFIX = "RECAP_DATA";
export const VIEW_ONLY_PASSCODE_KEY = "VIEW_ONLY_PASSCODE_KEY";

export const linkRegex = /\b(?:https?:\/\/|www\.)[^\s<>"]+[^.,:;"')\]\s<>]/gi;

export const replaceLinksWithAnchorTags = value => {
  return value.replace(linkRegex, match => {
    const url = match.startsWith("www.") ? `https://${match}` : match;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${match}</a>`;
  });
};

export const openAiSyncSourceUrl = url => {
  if (!url) {
    return;
  }
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  window.open(href, "_blank", "noopener,noreferrer");
};

export const userTypes = {
  USER: "user",
  OWNER: "owner",
  ADMIN: "admin",
  MANAGER: "manager",
  SUB_USER: "subuser"
};

export const wlSetupType = {
  ADMIN_WR_CLIENT_WR: "admin_write, client_write",
  ADMIN_WR_CLIENT_RO: "admin_write, client_read"
};

export const shareCapabilities = {
  ASSET: "asset",
  DEBT: "debt",
  NETWORTH: "networth",
  RECAP: "recap",
  INSURANCE: "insurance",
  DOCUMENT: "document",
  PLANNING: "planning",
  OWNERSHIP: "ownership",
  ALL: "all"
};

// Always a fresh v3 token, even while the v2 widget is showing (its token is
// single use and has usually been spent already by the request it guarded).
export const getRecaptchaV3Token = async action => {
  await loadRecaptcha(providerScripts.RECAPTCHA_V3).catch(() => {});
  // grecaptcha only exposes execute() once ready() has fired
  if (!window.grecaptcha?.ready) {
    throw new Error("reCAPTCHA not loaded");
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      try {
        window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_ID, { action }).then(resolve, reject);
      } catch (error) {
        reject(error);
      }
    });
  });
};

export const getSiteWhiteLabelConfigId = host => {
  // Set Whitelabel mode
  // return "alpha";
  // return "dev";

  return window.getSiteWhiteLabelConfigId(host);
};

export const isAppInWhiteLabelMode = () => {
  const whiteLabelId = getSiteWhiteLabelConfigId();
  return !whiteLabelId === false;
};

export const WLK_DOMAIN_COOKIE = "wlk-domain";

const wlkDomainCookieOptions = () => {
  const options = { path: "/", sameSite: "None", secure: true, expires: 365 };
  if (window.location.host.endsWith("kubera.com")) {
    options.domain = ".kubera.com";
  }
  return options;
};

export const setWlkDomainCookie = host => {
  Cookies.set(WLK_DOMAIN_COOKIE, host, wlkDomainCookieOptions());
};

export const setWlkDomainCookieForCurrentHost = () => {
  if (window.location.host.endsWith("kubera.com") === false) {
    return;
  }
  setWlkDomainCookie(window.location.host);
};

export const clearWlkDomainCookie = () => {
  const options = { path: "/" };
  if (window.location.host.endsWith("kubera.com")) {
    options.domain = ".kubera.com";
  }
  Cookies.remove(WLK_DOMAIN_COOKIE, options);
};

export const getWlkDomainCookie = () => {
  return Cookies.get(WLK_DOMAIN_COOKIE);
};

const WLK_DOMAIN_HOST_REGEX = /^[a-z0-9.-]+(:\d+)?$/i;

export const shouldRedirectToWlkDomain = () => {
  const wlkHost = getWlkDomainCookie();
  return !wlkHost === false && wlkHost !== window.location.host && WLK_DOMAIN_HOST_REGEX.test(wlkHost) === true;
};

export const redirectToWlkDomain = () => {
  if (shouldRedirectToWlkDomain() === false) {
    return false;
  }

  const { pathname, search, hash } = window.location;
  window.location.replace(`https://${getWlkDomainCookie()}${pathname}${search}${hash}`);
  return true;
};

const VIEW_MODE_ROUTE = "/view";
export const isAppInViewMode = () => {
  return window.location.pathname.startsWith(VIEW_MODE_ROUTE);
};

export const PROOF_BASE_ROUTE = "/proof";
export const isAppInProofViewMode = () => {
  const path = window.location.pathname;
  const params = getQueryParams(window.location);
  return path.startsWith(PROOF_BASE_ROUTE) && !!params?.t;
};

export const isAppInShareMode = () => isAppInViewMode() || isAppInProofViewMode();

const EXTENSION_PANEL_ROUTE = "/ai_sync_extension_panel";
export const isAppInExtensionMode = () => {
  return window.location.pathname.startsWith(EXTENSION_PANEL_ROUTE);
};

export const getSWConsts = () => {
  return {
    SIGNOUT_USER,
    RECAP_DATA_CHUNK_KEY_PREFIX,
    STORE_PORTFOLIO_DATA: "STORE_PORTFOLIO_DATA"
  };
};

export const getQueryParams = location => {
  return queryString.parse(location.search);
};

export const getTokenForViewMode = (checkSavedToken = true) => {
  const params = getQueryParams(window.location);
  if (!params === false && (!params.token === false || !params.t === false)) {
    return params.token || params.t;
  }
  const savedToken = sessionStorage.getItem(VIEW_ONLY_TOKEN_KEY);
  if (!savedToken === false) {
    return savedToken;
  }
  return null;
};

export const getSubscriptionRemainingDays = (accountCurrentTs, accountEndTs) => {
  const subscriptionCurrentTs = accountCurrentTs * 1000;
  const subscriptionExpiryTs = accountEndTs * 1000;
  const dateCurrentTs = new Date(subscriptionCurrentTs);
  const dateExpiryTs = new Date(subscriptionExpiryTs);
  return Math.ceil((dateExpiryTs.getTime() - dateCurrentTs.getTime()) / 86400000);
};

export const getCapitalizedStr = (str, lowerCaseRest = false) => {
  if (!str) {
    return str;
  }
  if (!lowerCaseRest) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const capitalizeStringWithSpaces = name => {
  if (!name === true) {
    return name;
  }
  const parts = name.trim().split(" ");
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    parts[i] = getCapitalizedStr(part);
  }
  return parts.join(" ");
};

let recaptchaV2Token = null;
let recaptchaV2WidgetId = null;
export let isV2WidgetLoaded = false;

export const USER_LAMBDA_VALIDATION_EXCEPTION = "UserLambdaValidationException";
export const cognitoErrorCodes = {
  CODE_MISMATCH: "CodeMismatchException",
  EXPIRED_CODE: "ExpiredCodeException",
  USERNAME_EXISTS: "UsernameExistsException",
  INVALID_PASSWORD: "InvalidPasswordException",
  USER_NOT_CONFIRMED: "UserNotConfirmedException",
  USER_NOT_FOUND: "UserNotFoundException",
  NOT_AUTHORIZED: "NotAuthorizedException",
  INVALID_PARAMETER: "InvalidParameterException"
};
export const recaptchaTypes = { V2: "v2", V3: "v3" };
export const parentActions = {
  SIGNIN: "signin",
  SIGNUP: "signup",
  SET_PASSWORD: "setPassword",
  NEW_PASSWORD: "newpassword"
};

window.verifyRecaptchaV2Callback = function(token) {
  recaptchaV2Token = token;
};

export const providerScripts = {
  YODLEE: "yodlee",
  PLAID: "plaid",
  FINICITY: "finicity",
  TREZOR: "trezor",
  RECAPTCHA_V2: "recaptchaV2",
  RECAPTCHA_V3: "recaptchaV3",
  META_PIXEL: "metaPixel"
};

const loadRecaptcha = version => {
  if (!window.loadProviderScript) {
    return Promise.reject(new Error("reCAPTCHA not loaded"));
  }
  return window.loadProviderScript(version);
};

export const showRecaptchaV2Widget = async onCheckCallback => {
  if (!process.env.REACT_APP_RECAPTCHA_SITE_ID_V2) return false;

  try {
    recaptchaV2Token = null;
    await loadRecaptcha(providerScripts.RECAPTCHA_V2);
    recaptchaV2WidgetId = window.grecaptcha.render("html_element", {
      sitekey: process.env.REACT_APP_RECAPTCHA_SITE_ID_V2,
      callback: token => {
        window.verifyRecaptchaV2Callback(token);
        onCheckCallback();
      }
    });
    isV2WidgetLoaded = true;
    return true;
  } catch (_) {
    return false;
  }
};

export const getRecaptchaTokenType = () => {
  return isV2WidgetLoaded ? recaptchaTypes.V2 : recaptchaTypes.V3;
};

export const getRecaptchaToken = async action => {
  await loadRecaptcha(providerScripts.RECAPTCHA_V3).catch(() => {});

  return new Promise((resolve, reject) => {
    if (!window.grecaptcha?.ready) {
      const error = new Error("reCAPTCHA not loaded");
      reject(error);
      return;
    }

    if (isV2WidgetLoaded && recaptchaV2Token) {
      resolve(recaptchaV2Token);
    } else if (isV2WidgetLoaded) {
      const error = {
        code: USER_LAMBDA_VALIDATION_EXCEPTION,
        message: "Please complete the reCAPTCHA verification before submitting the form."
      };
      reject(error);
    }
    window.grecaptcha.ready(function() {
      try {
        window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_ID, { action: action }).then(resolve, reject);
      } catch (error) {
        error.message = "reCAPTCHA Error";
        reject(error);
      }
    });
  });
};

export const resetRecaptchaV2 = () => {
  if (getRecaptchaTokenType() === recaptchaTypes.V2) {
    recaptchaV2Token = null;
    try {
      window.grecaptcha.reset(recaptchaV2WidgetId);
    } catch (_) {}
  }
};

export const VIEW_ONLY_USER_NAME = "VIEW_ONLY_USER_NAME";

export const setUserNameForViewMode = userName => {
  if (isAppInViewMode() === false) {
    return;
  }
  if (!userName === true) {
    sessionStorage.removeItem(VIEW_ONLY_USER_NAME);
  } else {
    sessionStorage.setItem(VIEW_ONLY_USER_NAME, userName);
  }
};

export const getAuthToken = async options => {
  const result = await Auth.currentSession(options)
    .then(data => {
      return "Bearer " + data.accessToken.toString();
    })
    .catch(() => {
      return "";
    });
  return result;
};

export const getIdToken = async () => {
  const result = await Auth.currentSession()
    .then(data => {
      return data.idToken.toString();
    })
    .catch(() => {
      return "";
    });
  return result;
};

export const isCustomWhiteLabelHost = () => {
  const whiteLabelConfigId = getSiteWhiteLabelConfigId();
  if (!whiteLabelConfigId === true || whiteLabelConfigId.split(".").length === 1) {
    return false;
  }
  return whiteLabelConfigId.endsWith(".kubera.com") === false;
};

export const getPasscodeForViewMode = () => {
  if (isAppInViewMode() === false) {
    return null;
  }
  return sessionStorage.getItem(VIEW_ONLY_PASSCODE_KEY);
};

export const showPasscodeScreen = () => {
  var url = Routes.PASSCODE;
  window.location.href = url + `?t=${getTokenForViewMode()}`;
};

export var Navigator;
export var Routes;
export const setupNavigator = (instance, routes) => {
  Navigator = instance;
  Routes = routes;
};

export const utf8ToHex = message => {
  const encoder = new TextEncoder();
  const messageData = encoder.encode(message);

  // Convert to Hex string
  return Array.from(messageData)
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
};

export const xorEncode = (text, key) => {
  if (!key === true) {
    return null;
  }
  const encoder = new TextEncoder();
  const textBytes = encoder.encode(text);
  const keyBytes = encoder.encode(key);
  let encoded = "";
  for (let i = 0; i < textBytes.length; i++) {
    encoded += String.fromCharCode(textBytes[i] ^ keyBytes[i % keyBytes.length]);
  }
  return btoa(encoded);
};
